import React, { useState } from 'react';
import Select, {
  components,
  type InputActionMeta,
  type InputProps,
  type SingleValue,
} from 'react-select';

interface OrganisationOption {
  readonly value: string;
  readonly label: string;
}

interface OrganisationSelectProps {
  readonly organisations: readonly string[];
  readonly value: string | null;
  readonly onChange: (organisation: string | null) => void;
  readonly isLoading?: boolean;
  readonly isDisabled?: boolean;
  readonly inputId?: string;
}

function OrganisationSelectInput(
  props: InputProps<OrganisationOption, false>,
): React.ReactElement {
  return (
    <components.Input
      {...props}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="none"
      spellCheck="false"
      data-lpignore="true"
      data-1p-ignore="true"
      data-form-type="other"
    />
  );
}

function OrganisationSelect({
  organisations,
  value,
  onChange,
  isLoading = false,
  isDisabled = false,
  inputId = 'organisation',
}: OrganisationSelectProps) {
  const [inputValue, setInputValue] = useState('');

  const options: OrganisationOption[] = organisations.map((organisation) => ({
    value: organisation,
    label: organisation,
  }));

  const selectedOption =
    value === null
      ? null
      : (options.find((option) => option.value === value) ?? {
          value,
          label: value,
        });

  const handleChange = (option: SingleValue<OrganisationOption>) => {
    onChange(option?.value ?? null);
    setInputValue('');
  };

  const handleInputChange = (
    nextValue: string,
    actionMeta: InputActionMeta,
  ): void => {
    if (
      actionMeta.action === 'input-blur' ||
      actionMeta.action === 'menu-close' ||
      actionMeta.action === 'set-value'
    ) {
      setInputValue('');
      return;
    }
    if (actionMeta.action === 'input-change') {
      setInputValue(nextValue);
    }
  };

  return (
    <Select<OrganisationOption>
      inputId={inputId}
      name="xa-organisation"
      className="custom_select xa-auth-organisation-select"
      classNamePrefix="custom_select"
      options={options}
      value={selectedOption}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      components={{ Input: OrganisationSelectInput }}
      isLoading={isLoading}
      isDisabled={isDisabled}
      isSearchable
      isClearable={false}
      blurInputOnSelect
      openMenuOnFocus={false}
      placeholder={
        isLoading ? 'Loading organisations...' : 'Select organisation...'
      }
      noOptionsMessage={() => 'No organisations found'}
      menuPlacement="bottom"
      maxMenuHeight={240}
    />
  );
}

export default OrganisationSelect;
